import { WarningAmberOutlined } from '@mui/icons-material'
import { Button, Typography } from '@mui/material'
import { RJSFValidationError } from '@rjsf/utils'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { WorkingStepsController } from 'controllers/WorkingStepsController'
import { useAppController } from 'customHooks/useAppController'
import { WorkingStepResourceSelector } from 'features/BillOfMaterials/WorkingSteps/WorkingStepResourceSelector'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import WorkingStepAutoComplete from 'features/WorkingSteps/WorkingStepAutoComplete'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import Sortable from 'sortablejs'
import { ShowException } from 'store/Application/appActions'
import { useAppSelector } from 'store/configureStore'
import {
  AvailableWorkingStepDto,
  WorkingStepType,
} from '../../../../services/APIs/InternalAPI/internal-api.contracts'
import {
  getWorkingStepKey,
  WorkingStepWithData,
} from './types/WorkingStepWithData'
import { JsonViewer } from 'components/Common/JsonViewer'

type Validations = Partial<Record<string, RJSFValidationError[]>>

const emptyGuid = '00000000-0000-0000-0000-000000000000'

function restoreWorkingStepPosition(event: Sortable.SortableEvent) {
  if (event.oldIndex === undefined) {
    return
  }

  const siblingsWithoutMovedItem = Array.from(event.from.children).filter(
    (child) => child !== event.item,
  )
  const referenceElement = siblingsWithoutMovedItem[event.oldIndex] ?? null

  event.from.insertBefore(event.item, referenceElement)
}

type WorkingStepSelectorProps = {
  affectedBomItemPointers: Array<BomItemPointer>
  bomItemPointer: BomItemPointer
  currentWorkingSteps: WorkingStepWithData[]
  availableWorkingSteps: AvailableWorkingStepDto[]
  activeWorkingStepId: string | null
  onActiveWorkingStepChange: (workingStep: WorkingStepWithData) => void
  onWorkingStepAdded: (workingStepType: WorkingStepType) => Promise<void>
  onWorkingStepsReordered: (
    movedActivityId: string,
    moveAfterActivityId?: string,
  ) => void
  onWorkingStepRemoved: (workingStep: WorkingStepWithData) => void
  markDirty: (onSave: () => void | Promise<void>, actionName: string) => void
  handleSave: (actionName: string) => void | Promise<void>
  validations: Validations | null
  disableResourceSelectors?: boolean
}

export const WorkingStepSelectorPanel = ({
  currentWorkingSteps,
  availableWorkingSteps,
  activeWorkingStepId,
  onActiveWorkingStepChange,
  onWorkingStepAdded,
  onWorkingStepsReordered,
  affectedBomItemPointers,
  onWorkingStepRemoved,
  validations,
  disableResourceSelectors,
}: WorkingStepSelectorProps) => {
  const getLocalizedWorkingStep = useLocalizedWorkingStepLabel()
  const workingStepsListRef = useRef<HTMLUListElement>(null)
  const { controller } = useAppController(() => new WorkingStepsController())
  const projectIsEditable = useAppSelector(
    projectSelectors.ProjectEditableStateSelector,
  )
  const isBuyerView = useAppSelector(
    projectSelectors.projectInBuyerViewSelector,
  )
  const canReorderWorkingSteps = projectIsEditable && !isBuyerView

  const availableWorkingStepTypes = useMemo(
    () => availableWorkingSteps.map((x) => x.secondaryWorkingStep),
    [availableWorkingSteps],
  )

  // The working step types that cannot be added again (already selected, not canBeAddedMoreThanOnce)
  const selectedOnceOnlyTypes = useMemo(() => {
    const canBeAddedMoreThanOnceTypes = new Set(
      availableWorkingSteps
        .filter((x) => x.canBeAddedMoreThanOnce)
        .map((x) => x.secondaryWorkingStep),
    )
    const currentTypes = new Set(
      currentWorkingSteps
        .map((ws) => ws.secondaryWorkingStep)
        .filter((t) => !canBeAddedMoreThanOnceTypes.has(t)),
    )
    return currentTypes
  }, [availableWorkingSteps, currentWorkingSteps])

  // Stable callback ref so the memoized WorkingStepAutoComplete never
  // re-renders due to onWorkingStepAdded changing identity.
  const onWorkingStepAddedRef = useRef(onWorkingStepAdded)
  onWorkingStepAddedRef.current = onWorkingStepAdded

  const selectedOnceOnlyTypesRef = useRef(selectedOnceOnlyTypes)
  selectedOnceOnlyTypesRef.current = selectedOnceOnlyTypes

  const repeatableWorkingStepTypes = useMemo(() => {
    return new Set(
      availableWorkingSteps
        .filter((x) => x.canBeAddedMoreThanOnce)
        .map((x) => x.secondaryWorkingStep),
    )
  }, [availableWorkingSteps])

  const repeatableWorkingStepTypesRef = useRef(repeatableWorkingStepTypes)
  repeatableWorkingStepTypesRef.current = repeatableWorkingStepTypes

  const addingWorkingStepRef = useRef(false)

  const stableOnWorkingStepAdded = useCallback(
    async (workingStepType: WorkingStepType) => {
      // Guard: prevent adding items that can only be added once
      if (selectedOnceOnlyTypesRef.current.has(workingStepType)) {
        return
      }
      // Guard: prevent duplicate calls while a previous add is in flight
      if (addingWorkingStepRef.current) {
        return
      }
      addingWorkingStepRef.current = true
      try {
        await onWorkingStepAddedRef.current(workingStepType)
      } finally {
        addingWorkingStepRef.current = false
      }
    },
    [],
  )

  // filterOptions uses a ref so its identity is stable — the memo'd
  // AutoComplete never re-renders due to this changing.
  const filterOptions = useCallback(
    (options: WorkingStepType[]) =>
      options.filter((option) => !selectedOnceOnlyTypesRef.current.has(option)),
    [],
  )

  const keepFilterAfterSelect = useCallback((option: WorkingStepType) => {
    return repeatableWorkingStepTypesRef.current.has(option)
  }, [])

  const autoCompleteProps = useMemo(
    () => ({
      disableCloseOnSelect: true as const,
      filterOptions,
    }),
    [filterOptions],
  )

  // Imperatively hide/show listbox options when selections change.
  // This avoids re-rendering the Autocomplete (which would reset scroll).
  useEffect(() => {
    const listbox = document.querySelector(
      '[role="listbox"]',
    ) as HTMLElement | null
    if (!listbox) return

    const options = listbox.querySelectorAll('[role="option"]')
    options.forEach((optionEl) => {
      const value = optionEl.getAttribute('data-value')
      const htmlEl = optionEl as HTMLElement
      if (value && selectedOnceOnlyTypes.has(value as WorkingStepType)) {
        htmlEl.style.display = 'none'
      } else {
        htmlEl.style.display = ''
      }
    })
  }, [selectedOnceOnlyTypes])

  const onWorkingStepOrderChanged = useCallback(
    async (event: Sortable.SortableEvent) => {
      const movedActivityId = event.item.dataset.workingStepActivityId

      if (
        !movedActivityId ||
        event.oldIndex === undefined ||
        event.newIndex === undefined ||
        event.oldIndex === event.newIndex
      ) {
        return
      }

      let previousElementSibling = event.item
        .previousElementSibling as HTMLLIElement | null
      let moveAfterActivityId: string | undefined

      while (previousElementSibling) {
        moveAfterActivityId =
          previousElementSibling.dataset.workingStepActivityId

        if (moveAfterActivityId) {
          break
        }

        previousElementSibling =
          previousElementSibling.previousElementSibling as HTMLLIElement | null
      }

      try {
        await controller.BatchUpdateWorkingStepsPosition({
          items: affectedBomItemPointers.map((bomItemPointer) => ({
            bomItemId: bomItemPointer.id,
            bomItemActivityId: movedActivityId,
            moveAfterBomItemActivityId: moveAfterActivityId,
          })),
        })

        onWorkingStepsReordered(movedActivityId, moveAfterActivityId)
      } catch (error) {
        ShowException('working step position', error)
        restoreWorkingStepPosition(event)
      }
    },
    [affectedBomItemPointers, controller, onWorkingStepsReordered],
  )

  useEffect(() => {
    if (!canReorderWorkingSteps || !workingStepsListRef.current) {
      return undefined
    }

    const sortable = Sortable.create(workingStepsListRef.current, {
      animation: 150,
      draggable: '[data-working-step-activity-id]',
      fallbackOnBody: true,
      ghostClass: 'ws__sortable-ghost',
      chosenClass: 'ws__sortable-ghost',
      onEnd: onWorkingStepOrderChanged,
    })

    return () => {
      sortable.destroy()
    }
  }, [canReorderWorkingSteps, onWorkingStepOrderChanged])

  return (
    <div className={'ws__list__root'}>
      <div className={'ws__search'}>
        <WorkingStepAutoComplete
          availableWorkingSteps={availableWorkingStepTypes}
          onOptionSelected={stableOnWorkingStepAdded}
          selectedOption={null}
          placeholder="available working steps"
          keepFilterAfterSelect={keepFilterAfterSelect}
          AutoCompleteProps={autoCompleteProps}
        />
      </div>

      <div className="ws__list__container">
        <div className={'ws__list__title'}>
          <Typography variant="caption" fontWeight={600}>
            included working steps
          </Typography>
        </div>
        <ul className={'ws__list'} ref={workingStepsListRef}>
          {currentWorkingSteps.map((ws, index) => {
            const wsType = ws.secondaryWorkingStep
            const hasPersistedActivityId =
              ws.bomItemActivityId && ws.bomItemActivityId !== emptyGuid
            const key = hasPersistedActivityId
              ? ws.bomItemActivityId
              : `${wsType}_${index}`
            return (
              <li
                key={key}
                className={`ws__item ${canReorderWorkingSteps && hasPersistedActivityId ? 'ws__sortable' : ''} ${activeWorkingStepId === key ? 'ws__active' : ''}`}
                data-working-step-activity-id={
                  hasPersistedActivityId ? ws.bomItemActivityId : undefined
                }
                onClick={() => {
                  onActiveWorkingStepChange(ws)
                }}
              >
                <div className={`ws__item__description`}>
                  <WorkingStepIconSvg
                    workingStepType={wsType}
                    attributes={{ width: '32px', height: '32px' }}
                    disableTooltip
                  />
                  <div>
                    <Typography variant="body2" fontWeight={600}>
                      {getLocalizedWorkingStep(wsType)}
                    </Typography>
                    <div>
                      <WorkingStepResourceSelector
                        bomItemPointersAndActivities={affectedBomItemPointers.map(
                          (bomItemPointer) => ({
                            bomItemPointer,
                            activityId: ws.bomItemActivityId,
                          }),
                        )}
                        activityWorkingStepType={wsType}
                        bomItemActivityId={ws.bomItemActivityId}
                        useStoreActivity
                        enableClickPropagation
                        withLabel
                        disabled={disableResourceSelectors}
                      />
                    </div>
                  </div>
                </div>
                <div className="ws__item__actions">
                  {validations?.[getWorkingStepKey(ws)]?.length ? (
                    <span>
                      <WarningAmberOutlined fontSize="small" color="warning" />
                      {/* <Typography variant="caption" color="warning">
                        {validations[getWorkingStepKey(ws)]?.length} issues
                      </Typography> */}
                    </span>
                  ) : null}
                  <Button
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation()
                      onWorkingStepRemoved(ws)
                    }}
                    data-testid="bom-details-remove-working-step"
                  >
                    remove
                  </Button>
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
